/**
 * Size chart drawer — cm/inch toggle for theme-rendered table cells.
 */

const CM_PER_IN = 2.54;
const IMPERIAL_COUNTRY_CODES = ['US', 'CA'];

/**
 * @returns {boolean}
 */
function prefersImperial() {
  const code = window.NOOMA_COUNTRY;
  return typeof code === 'string' && IMPERIAL_COUNTRY_CODES.includes(code);
}

/**
 * @param {number} value
 * @param {'cm' | 'inch'} unit
 */
function formatValue(value, unit) {
  const rounded = Math.round(value * 10) / 10;
  const display = rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(1);
  return unit === 'inch' ? `${display}in` : `${display}cm`;
}

/**
 * @param {HTMLElement} cell
 * @param {number} cmValue
 * @param {'cm' | 'inch'} unit
 */
function renderCell(cell, cmValue, unit) {
  const displayValue = unit === 'inch' ? cmValue / CM_PER_IN : cmValue;
  cell.textContent = formatValue(displayValue, unit);
}

/**
 * @param {HTMLElement} toggle
 */
function syncTogglePressedState(toggle) {
  for (const button of toggle.querySelectorAll('button[data-unit]')) {
    if (button instanceof HTMLButtonElement) {
      button.setAttribute('aria-pressed', button.classList.contains('is-active') ? 'true' : 'false');
    }
  }
}

/**
 * @param {HTMLElement} drawer
 * @param {'cm' | 'inch'} unit
 */
function setUnit(drawer, unit) {
  const toggle = drawer.querySelector('[data-dev-size-chart-unit-toggle]');
  const cells = drawer.querySelectorAll('[data-dev-size-chart-unit-value]');

  if (toggle instanceof HTMLElement) {
    for (const button of toggle.querySelectorAll('button[data-unit]')) {
      if (button instanceof HTMLButtonElement) {
        const isActive = button.dataset.unit === unit;
        button.classList.toggle('is-active', isActive);
      }
    }

    syncTogglePressedState(toggle);
  }

  for (const cell of cells) {
    if (!(cell instanceof HTMLElement)) {
      continue;
    }

    const cmValue = Number.parseFloat(cell.dataset.originalValue ?? '');
    if (Number.isNaN(cmValue)) {
      continue;
    }

    renderCell(cell, cmValue, unit);
  }
}

/**
 * @param {HTMLElement} drawer
 */
function initSizeChartDrawer(drawer) {
  if (drawer.getAttribute('data-dev-size-chart-init') === 'true') {
    return;
  }

  drawer.setAttribute('data-dev-size-chart-init', 'true');

  const toggle = drawer.querySelector('[data-dev-size-chart-unit-toggle]');
  const cells = drawer.querySelectorAll('[data-dev-size-chart-unit-value]');

  if (!cells.length) {
    return;
  }

  if (toggle instanceof HTMLElement) {
    toggle.addEventListener('click', (event) => {
      const button = event.target instanceof HTMLElement ? event.target.closest('button[data-unit]') : null;
      if (!(button instanceof HTMLButtonElement) || button.classList.contains('is-active')) {
        return;
      }

      setUnit(drawer, button.dataset.unit === 'inch' ? 'inch' : 'cm');
    });
  }

  const defaultUnitRaw = drawer.dataset.defaultUnit?.toLowerCase() ?? 'cm';
  const defaultUnit = defaultUnitRaw === 'inch' ? 'inch' : 'cm';
  const initialUnit = prefersImperial() ? 'inch' : defaultUnit;
  setUnit(drawer, initialUnit);
}

for (const drawer of document.querySelectorAll('[data-dev-size-chart-drawer]')) {
  if (drawer instanceof HTMLElement) {
    initSizeChartDrawer(drawer);
  }
}
